import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";

import { selectUserUid, selectUserCreditSummary } from "store/users/selectors";
import { selectUsersOrganization, selectUserOrgLoading } from "store/organizations/selectors";
import userActions from "store/users/actions";
import { ORGANIZATION_TYPES } from "utils/organization/constants";
import organizationActions from "store/organizations/actions";

import SectionHeading from "components/SectionHeading";
import Loading from "components/Loading";
import Select from "components/Input/Select";
import Button from "components/Button";
import OrgLogo from "./OrgLogo";
import MembersList from "./MembersList";
import RenameOrganizationModal from "./RenameOrganizationModal";
import JoinRequests from "./JoinRequests";
import CodeLibVisibility from "./CodeLibVisibility";
import DeleteOrganizationModal from "./DeleteOrganizationModal";
import CreateOrganizationModal from "features/CreateOrganizationModal";
import SnackbarAlert from "components/Alert";

import PencilIcon from "assets/images/icons/pencil.svg";

import classes from "./style.module.scss";

const { updateUserPrimaryOrg, updateUser, fetchUserData } = userActions;
const { fetchUserOrganization } = organizationActions;

const Organization = ({
    organization,
    orgLoading,
    uid,
    fetchUserOrganization,
    updateUserPrimaryOrg,
    creditSummary: { credits: totalCredits = 0 } = {},
    updateUser,
    fetchUserData,
}) => {
    const history = useHistory();

    const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [alert, setAlert] = useState({
        open: false,
        message: "",
        anchorOrigin: { vertical: "top", horizontal: "center" },
        direction: "down",
    });
    const [isChangingPrimaryOrg, setIsChangingPrimaryOrg] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    const { orgId = "" } = useParams();

    const { details = {}, memberOrgIds = {} } = organization;
    const {
        memberIds = [],
        adminIds = [],
        members = {},
        userRequestsToJoin = {},
        name = "",
        orgMeta = {},
        orgMeta: { orgType = "", logoUrl = "" } = {},
    } = details[orgId] || {};

    const { label: orgTypeLabel = "" } = ORGANIZATION_TYPES.find(({ value }) => value === orgType) || {};

    const membershipIds = Object.keys(memberOrgIds).filter((key) => key !== "primaryOrg");

    const IS_ADMIN = adminIds.includes(uid);
    // const IS_ADMIN = true;

    useEffect(() => {
        if (!orgId && memberOrgIds.primaryOrg) {
            history.push(`/user/organization/${memberOrgIds.primaryOrg}`);
        }

        if (uid && orgId) {
            fetchUserOrganization(uid, orgId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uid, fetchUserOrganization, orgId]);

    return (
        <>
            <SectionHeading title="Organization Settings" subtitle="Manage your organization" />
            <div>
                {orgLoading || isChangingPrimaryOrg ? (
                    <Loading />
                ) : membershipIds.length === 0 ? (
                    <div className={classes.emptyState}>
                        <h3>You are not part of an organization</h3>
                        <p>Create one to share a model folder, a code library and an upgrade library with your team.</p>
                        <Button onClick={() => setIsCreateModalOpen(true)}>Create Organization</Button>
                    </div>
                ) : (
                    <div className={classes.orgContainer}>
                        <section className={classes.identityCard}>
                            <div className={classes.logoSlot}>
                                <OrgLogo
                                    logoUrl={logoUrl}
                                    organizationDetails={details[orgId]}
                                    orgId={orgId}
                                    uid={uid}
                                    IS_ADMIN={IS_ADMIN}
                                    setAlert={setAlert}
                                />
                            </div>
                            <div className={classes.identityDetails}>
                                <div className={classes.orgName}>
                                    <h3>{name}</h3>
                                    {IS_ADMIN && (
                                        <img
                                            src={PencilIcon}
                                            alt="Rename organization"
                                            onClick={() => setIsRenameModalOpen(true)}
                                        />
                                    )}
                                </div>
                                <p className={classes.orgMeta}>
                                    {[orgTypeLabel, `${memberIds.length} member${memberIds.length === 1 ? "" : "s"}`]
                                        .filter(Boolean)
                                        .join(" · ")}
                                </p>
                                <Select
                                    className={classes.orgSelector}
                                    label={"Current Organization"}
                                    options={Object.entries(memberOrgIds)
                                        .filter(([key, _]) => key !== "primaryOrg")
                                        .map(([key, { name }]) => ({
                                            label: name,
                                            value: key,
                                        }))}
                                    input={{
                                        value: orgId,
                                        onChange: async (newOrgId) => {
                                            if (isChangingPrimaryOrg) return;

                                            setIsChangingPrimaryOrg(true);

                                            document.body.style.cursor = "wait";

                                            await updateUserPrimaryOrg(uid, newOrgId);

                                            history.push(`/user/organization/${newOrgId}`);

                                            document.body.style.cursor = "default";

                                            setIsChangingPrimaryOrg(false);
                                        },
                                    }}
                                />
                            </div>
                            <div className={classes.identityActions}>
                                <Button type="hollow" small smallPadding onClick={() => setIsCreateModalOpen(true)}>
                                    Create Organization
                                </Button>
                            </div>
                        </section>
                        <div className={classes.settingsColumn}>
                            {IS_ADMIN && <CodeLibVisibility orgId={orgId} orgMeta={orgMeta} setAlert={setAlert} />}
                            {IS_ADMIN && (
                                <div className={classes.dangerZone}>
                                    <div>
                                        <h3>Delete Organization</h3>
                                        <p>
                                            Removes the shared folder and the shared libraries for everyone. Models go
                                            back to the people who own them.
                                        </p>
                                    </div>
                                    <Button type="red" onClick={() => setIsDeleteModalOpen(true)}>
                                        Delete Organization
                                    </Button>
                                </div>
                            )}
                        </div>
                        <div className={classes.membersColumn}>
                            <JoinRequests
                                requests={IS_ADMIN ? userRequestsToJoin : {}}
                                uid={uid}
                                orgId={orgId}
                                fetchUserOrganization={() => fetchUserOrganization(uid, orgId)}
                                setAlert={setAlert}
                            />
                            <MembersList
                                membersList={members}
                                adminsList={adminIds}
                                memberIds={memberIds}
                                uid={uid}
                                orgId={orgId}
                                IS_ADMIN={IS_ADMIN}
                                fetchUserOrganization={() => fetchUserOrganization(uid, orgId)}
                                setAlert={setAlert}
                                totalCredits={totalCredits}
                                updateUser={updateUser}
                            />
                        </div>
                    </div>
                )}
            </div>
            {IS_ADMIN && (
                <RenameOrganizationModal
                    open={isRenameModalOpen}
                    onClose={() => setIsRenameModalOpen(false)}
                    uid={uid}
                    orgId={orgId}
                    oldName={name}
                    // the shared folder in the dashboard is named from the membership on the user
                    // document, not from the organization, so that copy has to be read back too
                    fetchUserOrganization={async () => {
                        await fetchUserData(uid);

                        await fetchUserOrganization(uid, orgId);
                    }}
                    setAlert={setAlert}
                />
            )}
            {IS_ADMIN && (
                <DeleteOrganizationModal
                    open={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    uid={uid}
                    orgId={orgId}
                    name={name}
                    memberCount={memberIds.length}
                    setAlert={setAlert}
                    onDeleted={async () => {
                        //the membership is gone, so the user data that drives every organization
                        //view has to be reread before leaving the page
                        await fetchUserData(uid);

                        history.push("/dashboard");
                    }}
                />
            )}
            {/* mounted on demand so each run starts on the first step */}
            {isCreateModalOpen && (
                <CreateOrganizationModal
                    open
                    onClose={() => setIsCreateModalOpen(false)}
                    // open the organization that was just created, and make it the one this
                    // account lands on, the same as picking it from the dropdown would
                    onCreated={async (newOrgId) => {
                        if (!newOrgId) return;

                        await updateUserPrimaryOrg(uid, newOrgId);

                        history.push(`/user/organization/${newOrgId}`);
                    }}
                />
            )}
            <SnackbarAlert
                top={"90%"}
                autoHideDuration={6000}
                onClose={() =>
                    setAlert({
                        open: false,
                        message: "",
                        anchorOrigin: { vertical: "top", horizontal: "center" },
                        direction: "down",
                    })
                }
                onExited={() => {}}
                {...alert}
            />
        </>
    );
};

const mapDispatchToProps = (dispatch) => ({
    fetchUserOrganization: (uid, orgId) => dispatch(fetchUserOrganization(uid, orgId)),
    fetchUserData: (uid) => dispatch(fetchUserData(uid)),
    updateUserPrimaryOrg: (uid, orgId) => dispatch(updateUserPrimaryOrg(uid, orgId)),
    updateUser: (data) => dispatch(updateUser(data)),
});

const mapStateToProps = createStructuredSelector({
    organization: selectUsersOrganization,
    orgLoading: selectUserOrgLoading,
    uid: selectUserUid,
    creditSummary: selectUserCreditSummary,
});

export default connect(mapStateToProps, mapDispatchToProps)(Organization);
